import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import Spinner from 'components/crm/ui/Spinner';
import Tag from 'components/crm/ui/Tag';
import CallListBanner from 'components/prospection/CallListBanner';
import CallListStates from 'components/prospection/CallListStates';
import CallRow, { CompanyCell } from 'components/prospection/CallRow';
import useText from 'hooks/useText';
import { dayState, splitLines } from 'pages/prospection/callListItems';

const RESULT_TONES = {
  answered: 'success',
  callback: 'orange',
  no_answer: 'neutral',
  not_interested: 'danger',
};

// Sticky header of the planned and queue blocks: title in ink, count in muted.
const BlockHeader = ({ title, count, children }) => (
  <div className="sticky top-0 z-10 flex h-12 items-center gap-3 bg-white">
    <span className="flex-1 font-title text-sm font-medium text-ink">
      {title} <span className="text-muted">· {count}</span>
    </span>
    {children}
  </div>
);

// A call made today, with the tag of its result; the cells are faded.
const DoneRow = ({ call, text }) => (
  <div
    data-testid="done-row"
    className="flex h-16 items-center gap-3 border-b border-line px-4"
  >
    <CompanyCell company={call.company} text={text} faded />
    <span className="flex shrink-0 items-center opacity-50">
      <Tag tone={RESULT_TONES[call.result] || 'neutral'} title>
        {text(`results.${call.result}`)}
      </Tag>
    </span>
  </div>
);

/**
 * The left column of the prospection page: the goals banner, then the planned
 * calls, the queue and the calls made today, or the empty and finished
 * states. Presentational: the page owns the data and every action.
 */
const CallList = ({
  day,
  loading,
  error,
  now,
  editing,
  selectedCompanyId,
  openCompanyId,
  queueActions,
  onCall,
  onRemove,
  onOpenGlobal,
  onAddProspect,
}) => {
  const { text } = useText('prospection');
  const [showDone, setShowDone] = useState(true);

  if (loading || (!day && !error)) {
    return (
      <div
        data-testid="call-list-loading"
        className="flex h-full min-h-[240px] items-center justify-center"
      >
        <Spinner />
      </div>
    );
  }
  if (error) {
    return (
      <p role="alert" className="p-4 text-sm leading-5 text-danger">
        {text('states.loadError', { error })}
      </p>
    );
  }

  const state = dayState(day);
  const { planned, queue } = splitLines(day.pending);
  // Most recent first, whatever order the calls came in.
  const made = [...(day.made || [])].sort((a, b) =>
    a.at < b.at ? 1 : a.at > b.at ? -1 : 0,
  );
  const rowOf = (line, rank) => {
    const companyId = line.company && line.company.id;
    return (
      <CallRow
        key={line.id}
        line={line}
        rank={rank}
        now={now}
        editing={editing}
        selected={!!selectedCompanyId && companyId === selectedCompanyId}
        removeDisabled={!!openCompanyId && companyId === openCompanyId}
        onCall={onCall}
        onRemove={onRemove}
      />
    );
  };

  return (
    <div className="flex h-full min-h-0 w-[520px] shrink-0 flex-col gap-4">
      <CallListBanner day={day} editing={editing} />
      <div className="min-h-0 flex-1 overflow-y-auto">
        {state === 'active' ? (
          <>
            {planned.length > 0 && (
              <section>
                <BlockHeader
                  title={text('blocks.plannedTitle')}
                  count={planned.length}
                />
                {planned.map((line) => rowOf(line, null))}
              </section>
            )}
            <section>
              <BlockHeader
                title={text('blocks.queueTitle')}
                count={queue.length}
              >
                {queueActions}
              </BlockHeader>
              {queue.length > 0 ? (
                queue.map((line, index) => rowOf(line, index + 1))
              ) : (
                <p className="px-4 py-4 text-sm leading-5 text-muted">
                  {text('blocks.queueEmpty')}
                </p>
              )}
            </section>
            {editing && (
              <button
                type="button"
                onClick={onOpenGlobal}
                className="mx-4 my-4 flex h-12 w-[calc(100%-2rem)] items-center justify-center rounded-lg border border-dashed border-lineStrong text-sm font-medium text-ink transition-colors hover:bg-gray-50"
              >
                {text('row.pickGlobal')}
              </button>
            )}
          </>
        ) : (
          <CallListStates
            state={state}
            made={made}
            onOpenGlobal={onOpenGlobal}
            onAddProspect={onAddProspect}
          />
        )}
        {made.length > 0 && (
          <section>
            <button
              type="button"
              aria-expanded={showDone}
              onClick={() => setShowDone((shown) => !shown)}
              className="flex h-12 w-full items-center justify-between border-b border-line px-4 font-title text-sm font-medium text-ink transition-colors hover:bg-gray-50"
            >
              {text('blocks.done')}
              <span
                data-testid="done-chevron"
                className={`flex transition-transform ${showDone ? '' : '-rotate-90'}`}
              >
                <ChevronDown size={20} />
              </span>
            </button>
            {showDone &&
              made.map((call) => (
                <DoneRow key={call.id} call={call} text={text} />
              ))}
          </section>
        )}
      </div>
    </div>
  );
};

export default CallList;
